import {
  EVALUATION_PLAN_SHEET_CANVAS_VERSION,
  EVALUATION_PLAN_SHEET_HEADER_BAND_NORM,
  createEmptySheetLayoutSubset,
  sheetLayoutSubsetToSlideLayout,
  slideLayoutToSheetLayoutSubset,
  type ProgramSlideLayoutSubsetV1,
} from './evaluationPlanSheetCanvas';
import { parseSlideLayoutJson, slideLayoutHasMeaningfulCanvasContent } from './programContentSlideCanvas';
import { formatWebDocumentAcademicYearLabel } from './evaluationPlanWebDocument';

export const LEARNING_CONTENT_WORKSHEET_TITLE = '활동지' as const;
/** 저장 데이터를 읽을 때 자르는 한도(예전 데이터 보호용). 편집기에서 늘릴 수 있는 쪽 수는 아래 값 */
export const LEARNING_CONTENT_WORKSHEET_MAX_PAGES = 30 as const;
/** 활동지 편집기에서 만들 수 있는 최대 쪽 수 */
export const LEARNING_CONTENT_WORKSHEET_EDITOR_MAX_PAGES = 2 as const;

export type LearningContentWorksheetDocumentV1 = {
  version: typeof EVALUATION_PLAN_SHEET_CANVAS_VERSION;
  slot: 'sheet';
  title: string;
  titleReadOnly: true;
  headerBandNorm: number;
  academicYearLabel: string;
  /** @deprecated 첫 페이지 미러 — `pages[0]`과 동기화 */
  layout: ProgramSlideLayoutSubsetV1;
  pages: ProgramSlideLayoutSubsetV1[];
  /**
   * AI가 만든 활동지의 원본 블록(선택). 캔버스 요소의 `groupId`가 블록 id와 이어진다.
   * '다른 구성으로 다시 만들기'에서 지난 블록 유형을 알 때 쓴다.
   */
  aiSource?: LearningContentWorksheetAiSourceV1;
};

export type LearningContentWorksheetAiSourceV1 = {
  v: 1;
  blocks: unknown[];
  /** 활동지 제목(다시 맞출 때 사용, 예전 데이터에는 없음) */
  title?: string;
  /** 단계 소제목 {도입: '…'} (예전 데이터에는 없음) */
  stageTitles?: Record<string, string>;
};

const AI_SOURCE_MAX_BLOCKS = 16;
const AI_SOURCE_MAX_JSON = 60000;

export function parseLearningContentWorksheetAiSource(raw: unknown): LearningContentWorksheetAiSourceV1 | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const obj = raw as { v?: unknown; blocks?: unknown; title?: unknown; stageTitles?: unknown };
  if (obj.v !== 1 || !Array.isArray(obj.blocks)) return undefined;
  const extra: Pick<LearningContentWorksheetAiSourceV1, 'title' | 'stageTitles'> = {};
  if (typeof obj.title === 'string') extra.title = obj.title.slice(0, 60);
  if (obj.stageTitles && typeof obj.stageTitles === 'object' && !Array.isArray(obj.stageTitles)) {
    const st: Record<string, string> = {};
    for (const [k, v] of Object.entries(obj.stageTitles as Record<string, unknown>).slice(0, 4)) {
      if (typeof v === 'string' && v.trim()) st[k.slice(0, 8)] = v.trim().slice(0, 30);
    }
    extra.stageTitles = st;
  }
  const blocks = obj.blocks
    .filter((b) => b && typeof b === 'object' && !Array.isArray(b))
    .slice(0, AI_SOURCE_MAX_BLOCKS);
  if (!blocks.length) return undefined;
  try {
    const json = JSON.stringify(blocks);
    if (json.length > AI_SOURCE_MAX_JSON) return undefined;
    return { v: 1, blocks: JSON.parse(json) as unknown[], ...extra };
  } catch {
    return undefined;
  }
}

/** aiSource 블록에 쓰인 유형 목록(중복 없이) */
export function worksheetAiSourceBlockTypes(doc: Pick<LearningContentWorksheetDocumentV1, 'aiSource'>): string[] {
  const out: string[] = [];
  for (const block of doc.aiSource?.blocks ?? []) {
    const type = (block as { type?: unknown })?.type;
    if (typeof type === 'string' && type && !out.includes(type)) out.push(type);
  }
  return out;
}

export {
  sheetLayoutSubsetToSlideLayout,
  slideLayoutToSheetLayoutSubset,
};

function parseSheetLayoutSubset(raw: unknown): ProgramSlideLayoutSubsetV1 {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return createEmptySheetLayoutSubset();
  }
  return slideLayoutToSheetLayoutSubset(parseSlideLayoutJson(raw));
}

export function getWorksheetDocumentPages(
  data: LearningContentWorksheetDocumentV1
): ProgramSlideLayoutSubsetV1[] {
  if (Array.isArray(data.pages) && data.pages.length > 0) {
    return data.pages.map((page) => parseSheetLayoutSubset(page));
  }
  return [parseSheetLayoutSubset(data.layout)];
}

export function normalizeLearningContentWorksheetDocument(
  data: LearningContentWorksheetDocumentV1
): LearningContentWorksheetDocumentV1 {
  const pages = getWorksheetDocumentPages(data).slice(0, LEARNING_CONTENT_WORKSHEET_MAX_PAGES);
  const safePages = pages.length ? pages : [createEmptySheetLayoutSubset()];
  return {
    ...data,
    title: typeof data.title === 'string' ? data.title : LEARNING_CONTENT_WORKSHEET_TITLE,
    pages: safePages,
    layout: safePages[0],
  };
}

export function createDefaultLearningContentWorksheetDocument(): LearningContentWorksheetDocumentV1 {
  const layout = createEmptySheetLayoutSubset();
  return {
    version: EVALUATION_PLAN_SHEET_CANVAS_VERSION,
    slot: 'sheet',
    title: LEARNING_CONTENT_WORKSHEET_TITLE,
    titleReadOnly: true,
    headerBandNorm: EVALUATION_PLAN_SHEET_HEADER_BAND_NORM,
    academicYearLabel: formatWebDocumentAcademicYearLabel(),
    layout,
    pages: [layout],
  };
}

export function parseLearningContentWorksheetDocument(raw: unknown): LearningContentWorksheetDocumentV1 | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const obj = raw as Partial<LearningContentWorksheetDocumentV1> & { layout?: unknown; pages?: unknown };
  if (obj.version !== EVALUATION_PLAN_SHEET_CANVAS_VERSION) return null;
  if (obj.slot !== 'sheet') return null;

  const headerBandNorm =
    typeof obj.headerBandNorm === 'number' && Number.isFinite(obj.headerBandNorm)
      ? Math.min(0.35, Math.max(0.08, obj.headerBandNorm))
      : EVALUATION_PLAN_SHEET_HEADER_BAND_NORM;

  const pages =
    Array.isArray(obj.pages) && obj.pages.length > 0
      ? obj.pages.map((page) => parseSheetLayoutSubset(page))
      : [parseSheetLayoutSubset(obj.layout)];

  const base: LearningContentWorksheetDocumentV1 = {
    version: EVALUATION_PLAN_SHEET_CANVAS_VERSION,
    slot: 'sheet',
    title:
      typeof obj.title === 'string' && obj.title.trim() ? obj.title.trim() : LEARNING_CONTENT_WORKSHEET_TITLE,
    titleReadOnly: true,
    headerBandNorm,
    academicYearLabel:
      typeof obj.academicYearLabel === 'string' && obj.academicYearLabel.trim()
        ? obj.academicYearLabel.trim()
        : formatWebDocumentAcademicYearLabel(),
    layout: pages[0],
    pages,
  };
  const aiSource = parseLearningContentWorksheetAiSource((obj as { aiSource?: unknown }).aiSource);
  if (aiSource) base.aiSource = aiSource;
  return normalizeLearningContentWorksheetDocument(base);
}

export function cloneLearningContentWorksheetDocument(
  data: LearningContentWorksheetDocumentV1
): LearningContentWorksheetDocumentV1 {
  return parseLearningContentWorksheetDocument(data) ?? createDefaultLearningContentWorksheetDocument();
}

export function worksheetPageLayoutHasContent(page: ProgramSlideLayoutSubsetV1): boolean {
  return slideLayoutHasMeaningfulCanvasContent(sheetLayoutSubsetToSlideLayout(page));
}

export function worksheetDocumentMeaningfulPageCount(data: LearningContentWorksheetDocumentV1): number {
  return getWorksheetDocumentPages(data).filter((page) => worksheetPageLayoutHasContent(page)).length;
}

export function worksheetDocumentHasContent(data: LearningContentWorksheetDocumentV1): boolean {
  return worksheetDocumentMeaningfulPageCount(data) > 0;
}
